import { getFeatureFlags } from "services/config.service";

const pendingFeatureFlagRequests = new Map<string, Promise<boolean>>();
const FEATURE_FLAGS_STORAGE_KEY = "FeatureFlags";
const FEATURE_FLAGS_CACHE_TTL_MS = 15 * 60 * 1000;

interface FeatureFlagCacheEntry {
  value: boolean;
  fetchedAt: number;
}

type FeatureFlagsCache = Record<string, FeatureFlagCacheEntry>;

const isFeatureFlagsCache = (value: unknown): value is FeatureFlagsCache => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).every((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return false;
    }

    const typedEntry = entry as Record<string, unknown>;
    return typeof typedEntry.value === "boolean" && typeof typedEntry.fetchedAt === "number";
  });
};

const persistFeatureFlagsCache = (cache: FeatureFlagsCache): void => {
  if (Object.keys(cache).length === 0) {
    localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
    return;
  }

  localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(cache));
};

const getFeatureFlagsCache = (): FeatureFlagsCache => {
  try {
    const storedFlags = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);

    if (!storedFlags) {
      return {};
    }

    const parsedFlags: unknown = JSON.parse(storedFlags);

    if (!isFeatureFlagsCache(parsedFlags)) {
      localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
      return {};
    }

    const now = Date.now();
    const activeFlags = Object.fromEntries(
      Object.entries(parsedFlags).filter(
        ([, entry]) => now - entry.fetchedAt < FEATURE_FLAGS_CACHE_TTL_MS
      )
    );

    if (Object.keys(activeFlags).length !== Object.keys(parsedFlags).length) {
      persistFeatureFlagsCache(activeFlags);
    }

    return activeFlags;
  } catch (error) {
    console.error("Erro ao ler feature flags do localStorage:", error);
    localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
    return {};
  }
};

const mergeFeatureFlagsIntoStorage = <T extends string>(flags: Record<T, boolean>): void => {
  const cachedFlags = getFeatureFlagsCache();
  const fetchedAt = Date.now();
  const updatedFlags = { ...cachedFlags };

  Object.keys(flags).forEach((key) => {
    updatedFlags[key] = { value: flags[key as T], fetchedAt };
  });

  persistFeatureFlagsCache(updatedFlags);
};

export const isFeatureEnabled = async (featureKey: string): Promise<boolean> => {
  try {
    // Primeiro, tentar buscar no localStorage
    const cachedFlags = getFeatureFlagsCache();
    const cachedFlag = cachedFlags[featureKey];

    if (cachedFlag) {
      return cachedFlag.value;
    }

    // Compartilhar a mesma requisição entre componentes que consultam a flag ao mesmo tempo.
    const pendingRequest = pendingFeatureFlagRequests.get(featureKey);
    if (pendingRequest) {
      return await pendingRequest;
    }

    // Se não encontrou no localStorage, buscar via API
    const request = getFeatureFlags({ keys: [featureKey] })
      .then((_result) => {
        const featureValue = _result?.FeatureFlags[0]?.Value ?? false;

        // Mesclar com o cache atual para preservar flags gravadas enquanto a chamada aguardava.
        mergeFeatureFlagsIntoStorage({ [featureKey]: featureValue });

        return featureValue;
      })
      .catch((error) => {
        console.error(`Erro ao buscar feature flag ${featureKey}:`, error);
        return false;
      })
      .finally(() => {
        pendingFeatureFlagRequests.delete(featureKey);
      });

    pendingFeatureFlagRequests.set(featureKey, request);
    return await request;
  } catch (error) {
    console.error(`Erro ao buscar feature flag ${featureKey}:`, error);
    return false;
  }
};

export const getFeatureFromLocalStorage = (featureKey: string): boolean => {
  try {
    return getFeatureFlagsCache()[featureKey]?.value ?? false;
  } catch (error) {
    console.error(`Erro ao buscar feature flag ${featureKey} do localStorage:`, error);
    return false;
  }
};

export const getFeatureFlagsByKeys = async <T extends string>(
  keys: T[]
): Promise<Record<T, boolean>> => {
  const body: GetFeatureFlagsRequest = { keys };

  const result: GetFeatureFlagsResponse = await getFeatureFlags(body);

  const flags = {} as Record<T, boolean>;

  keys.forEach((key) => {
    flags[key] = false; // default (caso não venha da API)
  });

  result?.FeatureFlags?.forEach((flag) => {
    if (keys.includes(flag.Key as T)) {
      flags[flag.Key as T] = flag.Value;
    }
  });

  return flags;
};

export const storageDailyDeliveryFeatureFlagAsync = async () => {
  try {
    const documentTypes = await getFeatureFlagsByKeys(["FeatureDailyDelivery"]);
    mergeFeatureFlagsIntoStorage(documentTypes);
  } catch (error) {
    // This flag is optional during application bootstrap. Keep the existing
    // cache and let the application continue when the feature-flags API fails.
    console.error("Erro ao armazenar a feature flag FeatureDailyDelivery:", error);
  }
};

export interface FeatureFlag {
  Key: string;
  Value: boolean;
}

export interface GetFeatureFlagsRequest {
  keys: string[];
}

export interface GetFeatureFlagsResponse {
  FeatureFlags: FeatureFlag[];
}
